import {
  ComponentType,
  ElementType,
  FC,
  KeyboardEvent,
  useEffect,
  useRef,
} from 'react';
import { useNavigate } from 'react-router';
import {
  Box,
  Flex,
  Heading,
  Icon,
  Input,
  Link,
  Stack,
  Text,
} from '@chakra-ui/react';

import { Button } from '~/core/middle-office/components';
import { ChevronLeft, IconGoShieldLock } from '../assets';
import { EMPTY_ARR, EMPTY_OBJ } from '../constants';
import {
  authenticatedSession,
  unauthenticatedSession,
  useAuthApi,
  useAuthData,
} from '../contexts/AuthContext';
import { useReturnUrl } from '../contexts/ReturnUrlContext';
import { useUserData } from '../contexts/UserContext';
import { useTwoFactorAuth } from '../hooks/useTwoFactorAuth.hook';
import { decodeJwtSub } from '../utils/authentication';
import { DateTimeDisplayMode } from './DateTimeDisplay/constants/DateTimeDisplayMode';
import { formatDateTime } from './DateTimeDisplay/logic/formatDateTime';
import { shouldLeaveVerifyScreenOnSync } from './twoFactorAuth/logic/shouldLeaveVerifyScreenOnSync';

interface TwoFactorAuthProps {
  onSuccessPath: string;
  adminPath: string;
  verifyCodePath: string;
  showBackButton?: boolean;
  backButtonAction?: () => void;
  headingText?: string;
  icon?: ElementType;
  styles?: any;
  LayoutComponent: ComponentType<any>;
  layoutProps?: Record<string, any>;
  loginPath: string;
  redirectPathsToIgnore?: readonly string[];
}

const convertSecondsToMinutesAndSeconds = (seconds: number) => {
  const date = new Date(new Date(0).setSeconds(seconds));

  return formatDateTime(date, DateTimeDisplayMode.Countdown);
};

export const TwoFactorAuth: FC<TwoFactorAuthProps> = ({
  onSuccessPath,
  adminPath,
  verifyCodePath,
  showBackButton = true,
  backButtonAction,
  headingText = 'Enter your authenticator code',
  icon: IconComponent = IconGoShieldLock,
  styles = EMPTY_OBJ,
  LayoutComponent,
  layoutProps,
  loginPath,
  redirectPathsToIgnore = EMPTY_ARR,
}) => {
  const { user } = useUserData();

  const {
    authCode,
    setAuthCode,
    error,
    errorExplainsCodeExpiry,
    isLoading,
    codeRequested,
    verifyCode,
    requestNewCode,
    codeRequestSupported,
    verifyCode: baseVerifyCode,
    resendCodeTimer,
  } = useTwoFactorAuth(user?.twoFactorMethod, loginPath);

  const navigate = useNavigate();
  const afterAuthDestination = useReturnUrl(
    onSuccessPath,
    adminPath,
    verifyCodePath,
    redirectPathsToIgnore,
  );
  const { setSession } = useAuthApi();
  const tokens = useAuthData();

  // Another tab completing verification for this same session syncs the
  // verified token here (the localStorage-backed session store's cross-tab
  // update); this tab then moves on rather than sitting on a form the session
  // no longer needs. Same-user only: see shouldLeaveVerifyScreenOnSync.
  const subAtMountRef = useRef(
    tokens?.jwt ? decodeJwtSub(tokens.jwt) : undefined,
  );
  useEffect(() => {
    const currentSub = tokens?.jwt ? decodeJwtSub(tokens.jwt) : undefined;

    if (
      shouldLeaveVerifyScreenOnSync(
        !!tokens?.twoFactorToken,
        subAtMountRef.current,
        currentSub,
      )
    ) {
      navigate(afterAuthDestination);
    }
    // afterAuthDestination is deliberately read, not depended on: it must not
    // re-trigger navigation when the return-url context shifts.
  }, [tokens?.twoFactorToken, tokens?.jwt, navigate]);

  const handleVerifyCode = async () => {
    const res = await baseVerifyCode();

    if (res.success) {
      const { twoFactorToken } = res.data.data;
      // Promote the current first-factor session to fully authenticated. No
      // current jwt is a bug state (there is nothing to verify against), so
      // fall back to a safe logged-out session rather than inventing one.
      setSession(
        tokens?.jwt
          ? authenticatedSession(
              tokens.jwt,
              tokens.expiresAt ?? '',
              twoFactorToken,
            )
          : unauthenticatedSession(),
      );
      navigate(afterAuthDestination);
    }
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLFormElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      handleVerifyCode();
    }
  };

  const isValidCode = resendCodeTimer > 0;

  const isTwoFactorByApp = user?.twoFactorMethod === 'app';

  return (
    <LayoutComponent {...layoutProps}>
      <Stack direction="column" gap={6} px={6} w="full">
        {showBackButton && backButtonAction && (
          <Flex
            alignItems="flex-start"
            color="navy.100"
            direction="row"
            mb={4}
            px={6}
          >
            <Link
              onClick={backButtonAction}
              style={{ color: 'navy.100', textDecoration: 'none' }}
            >
              <Flex alignItems="center" direction="row">
                <Icon as={ChevronLeft} boxSize={7} pr={2} />
                <Text color="navy.500" textAlign="left">
                  Back
                </Text>
              </Flex>
            </Link>
          </Flex>
        )}

        {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions */}
        <form
          onKeyDown={handleKeyDown}
          onSubmit={(e) => {
            e.preventDefault();
            verifyCode();
          }}
        >
          <Flex alignItems="center" direction="column" justifyContent="center">
            <Box mb={30}>
              <Icon as={IconComponent} boxSize="96px" />
            </Box>
            <Heading as="h2" mb={50} size="lg">
              {headingText}
            </Heading>
            <Stack direction="column" gap={4} w="full">
              <Box>
                <Text>Authenticator code*</Text>
              </Box>
              <Input
                aria-label="Authentication code"
                onChange={(event) => setAuthCode(event.target.value)}
                placeholder="Enter authentication code"
                pr={16}
                size="md"
                type="text"
                value={authCode}
                w="full"
                {...styles.loginInput}
              />
              {error && (
                <Text color="red" role="alert" textAlign="center">
                  {error}
                </Text>
              )}
            </Stack>
          </Flex>
        </form>

        <Button
          colorPalette="primary"
          loading={isLoading}
          onClick={handleVerifyCode}
          variant="solid"
          w="full"
        >
          Continue
        </Button>

        {isValidCode && !isTwoFactorByApp && (
          <Text textAlign="center">
            Code expires in {convertSecondsToMinutesAndSeconds(resendCodeTimer)}{' '}
            seconds. Didn’t receive a code?{' '}
            <Link as="button" onClick={() => requestNewCode()} type="button">
              Send new code
            </Link>
          </Text>
        )}

        {codeRequestSupported && !isValidCode && !isTwoFactorByApp && (
          <Text textAlign="center">
            {/* The hook reports whether the message already accounts for there
                being no code left to enter, so local copy only speaks when it
                does not. */}
            {!errorExplainsCodeExpiry && 'Code expired. '}
            {!codeRequested && (
              <Link as="button" onClick={() => requestNewCode()} type="button">
                Send new code
              </Link>
            )}
          </Text>
        )}
      </Stack>
    </LayoutComponent>
  );
};
